import React from "react";
import {Badge, Button, Card, CardBody, CardHeader, Container} from "reactstrap";
import {inject, observer} from "mobx-react";
import {Link} from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import TableGeneric from "../../../components/Akitatek/Headers/TableGeneric";
import Pagination from "../../../components/Akitatek/Pagination";
import getRoutes from "../../../components/Akitatek/getRoutes";
import {depuis} from "../../../util/sms";

const COULEURS = {pending: "secondary", sending: "info", sent: "success", failed: "danger"};

// Le téléphone interroge le site toutes les 10 secondes : au-delà d'une minute sans nouvelle, il y a un souci.
const SEUIL_OK = 60;
const SEUIL_ALERTE = 600;
const RAFRAICHISSEMENT_MS = 15000;

/**
 * Historique des SMS envoyés par la passerelle (téléphone Android), avec renvoi des SMS en échec.
 */
const SmsHistory = (inject("rootStore")(observer(class SmsHistory extends React.Component {

    get injected() {
        return this.props;
    }

    get store() {
        return this.injected.rootStore.smsStore;
    }

    constructor(props, context) {
        super(props, context);
        this.injected.rootStore.savedDatasStore.setHeader("Historique des SMS", "Messages envoyés par la passerelle SMS", "default.jpg");
    }

    componentDidMount() {
        this.store.currentPage = 1;
        this.store.getHistory();
        // Mise à jour discrète : l'heure du dernier contact serait périmée dès l'ouverture de la page.
        this.timer = setInterval(() => this.store.getHistory(true), RAFRAICHISSEMENT_MS);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    changeStatut(e, statut) {
        e.preventDefault();
        this.store.currentPage = 1;
        this.store.statut = statut;
        this.store.getHistory();
    }

    parentCallback = (page) => {
        this.store.currentPage = page;
        this.store.getHistory();
    };

    async resend(sms) {
        const result = await Swal.fire({
            title: "Renvoyer ce SMS ?",
            text: "Vers " + sms.numero,
            icon: "question",
            confirmButtonText: "Renvoyer",
            cancelButtonText: "Annuler",
            showCancelButton: true,
        });

        if (result.isConfirmed) {
            await this.store.resend(sms.id);
        }
    }

    row(sms) {
        const auteur = sms.auteur_nom ?? "—";

        return (
            <tr key={sms.id}>
                <td>{moment(sms.created_at).format("DD/MM/YYYY HH:mm")}</td>
                <td>{sms.numero}</td>
                <td style={{whiteSpace: "pre-wrap", maxWidth: "28em"}}>{sms.message}</td>
                <td className="text-center">
                    <Badge pill color={COULEURS[sms.statut] ?? "secondary"}>{this.store.statuts[sms.statut] ?? sms.statut}</Badge>
                    {sms.envoye_le && <div><small className="text-muted">{moment(sms.envoye_le).format("DD/MM HH:mm")}</small></div>}
                    {sms.erreur && <div><small className="text-danger">{sms.erreur}</small></div>}
                </td>
                <td className="text-center">{sms.tentatives}</td>
                <td>{auteur}</td>
                <td className="text-center">
                    {sms.statut === "failed" &&
                    <Button size="sm" color="warning" onClick={() => this.resend(sms)}>Renvoyer</Button>}
                </td>
            </tr>
        );
    }

    /** Dernière fois que le téléphone a joint le site, avec un voyant : vert, orange, rouge. */
    contact() {
        const contact = this.store.dernierContact;

        if (!contact) {
            return <><Badge pill color="danger">Jamais</Badge> Le téléphone ne s'est encore jamais connecté.</>;
        }

        const date = moment(contact);
        const secondes = Math.max(0, moment().diff(date, "seconds"));
        const couleur = secondes <= SEUIL_OK ? "success" : secondes <= SEUIL_ALERTE ? "warning" : "danger";

        return (
            <>
                <Badge pill color={couleur}>{secondes <= SEUIL_OK ? "En ligne" : "Sans nouvelle"}</Badge>
                {" "}Dernier contact du téléphone : <strong>{date.format("DD/MM/YYYY HH:mm:ss")}</strong> ({depuis(secondes)})
            </>
        );
    }

    render() {
        const store = this.store;

        return (
            <Container className="mt-2" fluid>
                <Card className="shadow">
                    <CardHeader style={{display: "flex", justifyContent: "space-between", flexWrap: "wrap"}}>
                        <h4 className="mb-0">Historique des SMS</h4>
                        <div style={{display: "flex", flexWrap: "wrap"}}>
                            <Button className="mr-2" color={store.statut === "" ? "primary" : "secondary"}
                                    onClick={e => this.changeStatut(e, "")}>Tous</Button>
                            {Object.keys(store.statuts).map(code => (
                                <Button key={code} className="mr-2"
                                        color={store.statut === code ? "primary" : "secondary"}
                                        onClick={e => this.changeStatut(e, code)}>
                                    {store.statuts[code]} <Badge color="light">{store.compteurs[code] ?? 0}</Badge>
                                </Button>
                            ))}
                            <Link to={getRoutes("Modèles SMS")}><Button color="info">Modèles de SMS</Button></Link>
                        </div>
                    </CardHeader>
                    <CardBody>
                        {store.messages.length === 0
                            ? <p className="text-muted">Aucun SMS pour l'instant.</p>
                            : <TableGeneric key="sms" titles={["Date", "Numéro", "Message", "Statut", "Essais", "Par", ""]}>
                                {store.messages.map(sms => this.row(sms))}
                            </TableGeneric>}
                        <Pagination currentPage={store.currentPage} lastPage={store.lastPage} parentCallback={this.parentCallback}/>
                        <p className="mt-3 mb-0" style={{textAlign: "center"}}>{this.contact()}</p>
                    </CardBody>
                </Card>
            </Container>
        );
    }
})));

export default SmsHistory;
